import React from 'react';
import { Button, notification, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { ItemType } from 'antd/lib/menu/hooks/useItems';

import { MainLayout } from 'components/Layout/MainLayout';
import { useQuery } from 'hooks/useQuery';
import { useAppDispatch, useAppSelector } from 'store';
import { PageTitles } from 'constants/pageTitles';
import { useDeepEffect } from 'utils/useDeepEffect';
import {
  deleteProject,
  loadInvestmentProjectsList,
  setUnPublishProject,
} from 'store/slices/investmentProjects/actions';
import { StatusesEnum } from 'constants/status';
import {
  InvestmentProjectsItem,
  QueryFormValues,
} from 'store/slices/investmentProjects/interfaces';
import { canBeUnPublished } from 'utils/rights';
import { StickyConfirmModal } from 'components/modals/StickyConfirmModal';
import { LangEnum } from 'constants/lang';

import { Filters } from './components/Filters';
import { TableProjects } from './components/TableProjects';

export const List = () => {
  const query = useQuery();
  const [isRemoveModalIsOpen, setRemoveModalIsOpen] =
    React.useState<boolean>(false);
  const [currentItem, setCurrentItem] = React.useState(null);

  const [sort, setSort] = React.useState<string | undefined>('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { b2bUrl } = useAppSelector(state => state.configSlice);
  const { list, total, limit } = useAppSelector(
    state => state.investmentProjects
  );

  const loadProjects = () =>
    dispatch(
      loadInvestmentProjectsList({ ...(query as QueryFormValues), sort })
    );

  const onCreateClick = () => navigate('add');

  const handleUnPublish = async (id: number) => {
    await dispatch(setUnPublishProject(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно снята с публикации',
        });
        loadProjects();
      });
  };

  useDeepEffect(() => {
    loadProjects();
  }, [query, sort]);

  const buttonList = [
    <Button key={1} onClick={onCreateClick} type='primary'>
      Создать
    </Button>,
  ];

  const onRemoveModalClose = () => {
    setRemoveModalIsOpen(false);
    setCurrentItem(null);
  };

  const onRemoveClick = async () => {
    await dispatch(deleteProject({ id: currentItem.id }))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно удалена',
        });
        loadProjects();
        onRemoveModalClose();
      });
  };

  const getActions = (item: InvestmentProjectsItem): ItemType[] => [
    {
      key: 'status',
      label: (
        <a
          target='_blank'
          rel='noreferrer nofollow'
          href={`${b2bUrl}/${item.lang}/invest-projects/${item.id}`}
        >
          На сайте
        </a>
      ),
      disabled: item.status !== StatusesEnum.PUBLISHED,
    },
    {
      key: 'edit',
      label: (
        <Link to={`/investmentProjects/${item.id}/edit`}>Редактировать</Link>
      ),
    },
    item.lang === LangEnum.ru && {
      key: 'add-translation',
      label: (
        <Link to={`/investmentProjects/${item.id}/add-translation`}>
          Создать копию для перевода
        </Link>
      ),
    },
    {
      key: 'unPublish',
      label: <Typography.Text>Снять с публикации</Typography.Text>,
      onClick: () => handleUnPublish(item?.id || 0),
      disabled: !canBeUnPublished(item),
    },
    {
      key: 'remove',
      label: 'Удалить',
      danger: true,
      onClick: () => {
        if (item.status === StatusesEnum.PUBLISHED) {
          notification.error({
            message: 'Необходимо снять сущность с публикации',
          });
        } else {
          setRemoveModalIsOpen(true);
          setCurrentItem(item);
        }
      },
    },
  ];

  return (
    <MainLayout
      pageTitle={PageTitles['investmentProjects']}
      extraButtonsList={buttonList}
      aside={<Filters />}
    >
      <TableProjects
        items={list}
        setSort={setSort}
        total={total}
        limit={limit}
        getActions={getActions}
      />
      <StickyConfirmModal
        isOpen={isRemoveModalIsOpen}
        onRequestClose={onRemoveModalClose}
        title='Удаление сущности'
        okText='Удалить'
        text='Вы действительно хотите удалить эту cущность?'
        onSubmit={onRemoveClick}
        okButtonProps={{ danger: true }}
      />
    </MainLayout>
  );
};
